<template>
  <div id="app">
    <router-view></router-view>

    <!-- 版本更新提示弹窗 -->
    <Modal
      v-model="updateModal.show"
      :title="updateModal.title"
      :closable="updateModal.closable"
      :mask-closable="false"
      :width="600"
      :styles="{top: '20px'}"
      class-name="update-modal"
    >
      <div class="update-content">
        <div class="update-text">
          <p style="font-size: 16px; font-weight: 500; margin-bottom: 8px;">发现新版本</p>
<!--          <p style="color: #666; margin-bottom: 12px;">检测到系统有新版本可用，建议立即更新以获得最佳体验。</p>-->
          <div v-if="updateModal.content" class="update-content-detail">
            <div v-if="typeof updateModal.content === 'string'" class="update-string">
              <p>{{ updateModal.content }}</p>
            </div>

            <div v-else-if="typeof updateModal.content === 'object'" class="update-object">
              <div v-for="(items, category) in updateModal.content" :key="category" class="update-category">
                <h4 class="category-title">{{ category }}</h4>
                <ul class="update-list">
                  <li v-for="(item, index) in items" :key="index" class="update-item">
                    {{ item }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div slot="footer">
        <Button v-if="updateModal.closable" @click="dismissUpdate" style="margin-right: 8px;">
          稍后更新
        </Button>
        <Button type="primary" @click="forceRefresh">
          立即更新
        </Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import _ from 'lodash'
import requestApi from '@/scripts/api'

export default {
  name: 'app',
  data () {
    return {
      updateModal: {
        show: false,
        title: '系统更新提示',
        closable: true,
        content: ''
      },
      localVersion: '',
      checkVersionTimer: null,
      delayUpdateTimer: null, // 稍后更新的延迟定时器

      eventLoop: {
        timer: null, // 10s cycle
        role: [
          { handle: this.getUnexecutedOrders, id: '176', tick: 6, current: 6 }
        ]
      }
    }
  },
  mounted () {
    // this.$router.replace({ name: 'login' })
    this.$nextTick(() => {
      this.checkVersion()
      // 每分钟检查一次版本
      this.checkVersionTimer = setInterval(this.checkVersion, 30 * 60000)
      this.eventLoop.timer = setInterval(() => {
        this.eventLoopController()
      }, 10 * 1000)
      this.eventLoopController()
    })
  },
  beforeDestroy () {
    // 清理所有定时器
    if (this.checkVersionTimer) {
      clearInterval(this.checkVersionTimer)
    }
    if (this.delayUpdateTimer) {
      clearTimeout(this.delayUpdateTimer)
    }
    clearInterval(this.eventLoop.timer)
  },
  created () {},
  methods: {
    /**
     * 检查版本更新
     */
    async checkVersion () {
      try {
        const finalResponse = await this.$http.reqDBResource(requestApi.DBApi.getVersion)

        if (!finalResponse.data.length || finalResponse.code !== 200) return

        const processedData = this.processVersionUpdates(
          finalResponse.data,
          localStorage.getItem('app_version') || '1968-01-01 00:00',
          sessionStorage.getItem('default_role_id')
        )

        if (processedData) {
          this.localVersion = processedData.latestVersion
          this.showUpdateModal(processedData.latestVersion, processedData.content, processedData.roleId, processedData.showRoleId)
        }
      } catch (error) {
        console.error('检查版本更新失败:', error)
      }
    },

    processVersionUpdates (dataArray, localVersion, currentRoleId) {
      const roleIdSet = new Set()
      const showRoleIdSet = new Set()
      const content = {}
      let latestVersion = localVersion

      for (const item of dataArray) {
        if (this.compareVersion(localVersion, item.version) >= 0) continue

        if (item.showRoleId && currentRoleId &&
            !item.showRoleId.split(',').some(id => id.trim() === currentRoleId)) continue

        if (this.compareVersion(latestVersion, item.version) < 0) {
          latestVersion = item.version
        }

        this.addRoleIds(item.roleId, roleIdSet)
        this.addRoleIds(item.showRoleId, showRoleIdSet)

        if (item.content) {
          for (const [category, items] of Object.entries(item.content)) {
            const key = `${category} (${item.version})`
            content[key] = Array.isArray(items) ? (content[key] || []).concat(items) : []
          }
        }
      }

      return Object.keys(content).length ? {
        latestVersion,
        content,
        roleId: Array.from(roleIdSet).join(','),
        showRoleId: Array.from(showRoleIdSet).join(',')
      } : null
    },

    addRoleIds (roleIdString, roleIdSet) {
      if (roleIdString) {
        roleIdString.split(',').forEach(id => {
          const trimmedId = id.trim()
          if (trimmedId) roleIdSet.add(trimmedId)
        })
      }
    },

    /**
     * 比较版本号（时间格式）
     * @param {string} localVersion 本地版本 (格式: 2025-10-09 11:00)
     * @param {string} remoteVersion 远程版本 (格式: 2025-10-09 11:00)
     * @returns {number} -1: 本地 < 远程, 0: 相等, 1: 本地 > 远程
     */
    compareVersion (localVersion, remoteVersion) {
      try {
        const localTime = new Date(localVersion).getTime()
        const remoteTime = new Date(remoteVersion).getTime()

        if (isNaN(localTime) || isNaN(remoteTime)) {
          console.warn('版本号格式无效:', { localVersion, remoteVersion })
          return -1
        }

        if (localTime < remoteTime) return -1
        if (localTime > remoteTime) return 1
        return 0
      } catch (error) {
        console.error('版本号比较失败:', error)
        return -1 // 出错时默认需要更新
      }
    },

    /**
     * 显示更新弹窗
     * @param {string} version 新版本号
     * @param {string} content 更新内容
     * @param {string} roleId 角色ID列表
     * @param {string} showRoleId 提示角色ID列表
     */
    showUpdateModal (version, content, roleId, showRoleId) {
      const currentRoleId = sessionStorage.getItem('default_role_id')

      let isForceUpdate = false
      if (roleId && currentRoleId) {
        const roleIdList = roleId.split(',').map(id => id.trim())
        isForceUpdate = roleIdList.includes(currentRoleId)
      }
      let isShow = true
      if (showRoleId && currentRoleId) {
        const showRoleIdList = showRoleId.split(',').map(id => id.trim())
        isShow = showRoleIdList.includes(currentRoleId)
      }

      this.updateModal = {
        show: isShow,
        title: '系统更新提示',
        closable: !isForceUpdate, // 如果是强制更新角色，则不允许关闭
        content: content || ''
      }

      console.log(`检测到新版本: ${version}, 当前角色: ${currentRoleId}, 强制更新: ${isForceUpdate}`)
    },

    /**
     * 强制刷新页面
     */
    forceRefresh () {
      window.localStorage.setItem('app_version', this.localVersion)
      setTimeout(() => {
        // 直接刷新
        window.location.reload(true)
      }, 10)
    },

    /**
     * 稍后更新（仅在允许关闭时可用）
     */
    dismissUpdate () {
      this.updateModal.show = false

      if (this.delayUpdateTimer) {
        clearTimeout(this.delayUpdateTimer)
      }

      if (this.checkVersionTimer) {
        clearInterval(this.checkVersionTimer)
        this.checkVersionTimer = null
      }

      // 10分钟后重新开始检查版本
      this.delayUpdateTimer = setTimeout(() => {
        this.checkVersion()
        // 重新启动定期检查
        this.checkVersionTimer = setInterval(this.checkVersion, 30 * 60000)
      }, 10 * 60 * 1000)

      console.log('已延迟更新，10分钟后重新检查版本')
    },

    eventLoopController () {
      console.log('tick')
      const currentRoleId = sessionStorage.getItem('default_role_id')
      this.eventLoop.role.forEach(item => {
        item.current++
        if (currentRoleId !== item.id) {
          this.$store.commit('inpatient/clearInpatientOrder')
        }
        if (item.current >= item.tick && currentRoleId === item.id) {
          item.current = 0
          item.handle()
        }
      })
    },
    /** 获取待执行的医嘱列表和护理级别 */
    getUnexecutedOrders () {
      const userId = sessionStorage.getItem('user_id')
      this.$http.reqDBResource(requestApi.DBApi.inpatientRecord.getUnexecutedOrders,
        {
          userId
        }
      ).then(res => {
        const { code, data } = res
        if (code === 200) {
          // console.log(data)
          const group = _.groupBy(data, (item) => {
            return `${item.department_id}_${item.bed_name}`
          })
          const tmp = {
            unchecked: [],
            unexecuted: [],
            unstoped: [],
            beds: {}
          }

          Object.entries(group).forEach(([k, v]) => {
            const obj = {
              level: null,
              details: v
            }
            let unchecked = false
            let unexecuted = false
            let unstoped = false
            v.forEach(item => {
              /** 取出护理级别 */
              if (/(.*级护理|新生儿护理|早产儿护理)/.test(item.subject_name)) {
                obj.level = item.subject_name
              }

              if (_.isEmpty(item.nurse_check_names)) {
                unchecked = true
              }
              if (_.isEmpty(item.stop_nurse_id) && _.isEmpty(item.stop_time) === false) {
                unstoped = true
              }
              if (item.actual_count < item.expected_count) {
                unexecuted = true
              }
            })
            obj.unchecked = unchecked
            obj.unstoped = unstoped
            obj.unexecuted = unexecuted
            tmp.beds[k] = obj
            // 设置全局标志
            if (unchecked) tmp.unchecked.push(k)
            if (unexecuted) tmp.unexecuted.push(k)
            if (unstoped) tmp.unstoped.push(k)
          })
          this.$store.commit('inpatient/setInpatientOrder', tmp)
        }
      })
    }
  }
}
</script>

<style lang="scss">
/* 自定义的通用样式库，注意先在style加上 lang="scss"，全局引用 */
@import './stylesheets/common.scss';
.ivu-message-error {
  max-width: 920px;
}

/* TODO: 这里的 text-align: center; 值得商榷（魏宏裕）*/
#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'San Francisco Pro', 'San Francisco', Roboto, 'IBM Plex Sans', 'Helvetica Neue',
    Helvetica, Verdana, Tahoma, Arial, 'Microsoft Yahei UI', 'Microsoft Yahei', 'PingFang SC', 'Noto Sans CJK SC', 'Source Han Sans SC', sans-serif,
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
  /*margin-top: 60px;*/
}

.row--hover {
  background-color: #eaf3ff;
}

.vxe-table-custom-0 {
  .vxe-body--column,
  .vxe-footer--column {
    padding: 4px 0 !important;
    font-size: 14px;
    font-weight: bold;
  }
}

.vxe-table-custom-1 {
  .vxe-input input {
    font-weight: bold;
    color: #606266;
  }
  .vxe-body--column,
  .vxe-footer--column {
    padding: 4px 0 !important;
    font-size: 16px;
    font-weight: bold;
  }
}

.vxe-table-custom-2 {
  .vxe-body--column,
  .vxe-footer--column {
    font-size: 18px;
    font-weight: bold;
  }
}

.update-modal {
  .ivu-modal-header {
    border-bottom: 1px solid #e8eaec;
    padding: 16px 24px;
    background: #f8f9fa;
  }

  .ivu-modal-body {
    padding: 12px 24px 12px 24px;
    max-height: 70vh;
    overflow-y: auto;
  }

  .update-content {
    display: flex;
    align-items: flex-start;

    .update-text {
      flex: 1;
      text-align: left;

      p {
        margin: 0;
        line-height: 1.5;
      }
    }
  }

  .update-content-detail {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    padding: 16px;
    border-radius: 8px;
    border-left: 4px solid #2d8cf0;
    margin-top: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

    .update-string {
      p {
        font-size: 14px;
        color: #666;
        margin: 0;
        line-height: 1.6;
        padding: 8px 12px;
        background: rgba(255, 255, 255, 0.6);
        border-radius: 4px;
        border: 1px solid #e1e4e8;
      }
    }

    .update-object {
      .update-category {
        margin-bottom: 16px;

        &:last-child {
          margin-bottom: 0;
        }

        .category-title {
          font-size: 15px;
          font-weight: 600;
          color: #2d8cf0;
          margin: 0 0 8px 0;
          padding: 6px 12px;
          background: rgba(45, 140, 240, 0.1);
          border-radius: 4px;
          border-left: 3px solid #2d8cf0;
          display: flex;
          align-items: center;
        }

        .update-list {
          margin: 0;
          padding: 0;
          list-style: none;
          background: rgba(255, 255, 255, 0.8);
          border-radius: 4px;
          border: 1px solid #e1e4e8;

          .update-item {
            font-size: 14px;
            color: #555;
            line-height: 1.6;
            padding: 8px 16px;
            border-bottom: 1px solid #f0f0f0;
            position: relative;
            transition: all 0.2s ease;

            &:last-child {
              border-bottom: none;
            }

            &::before {
              content: '✓';
              color: #52c41a;
              font-weight: bold;
              margin-right: 8px;
              font-size: 12px;
            }

            &:hover {
              background-color: rgba(45, 140, 240, 0.05);
              transform: translateX(2px);
            }
          }
        }
      }
    }
  }

  .ivu-modal-footer {
    border-top: 1px solid #e8eaec;
    padding: 12px 24px;
    text-align: right;
    background: #f8f9fa;
  }
}
</style>
